// Block types for the block-based note editor

export type BlockType =
  | 'text'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'bullet'
  | 'numbered'
  | 'checkbox'
  | 'code'
  | 'quote'
  | 'divider'
  | 'image';

export type CodeLanguage = 'js' | 'ts' | 'python' | 'css' | 'html' | 'sql' | 'json' | 'http';

export interface Block {
  id: string;
  type: BlockType;
  content: string;
  checked?: boolean; // for checkbox
  language?: CodeLanguage; // for code
  imageUrl?: string; // for image
  indent?: number; // for bullet/numbered (0-based)
}

export interface Note {
  id: string;
  title: string;
  blocks: Block[];
  pinned: boolean;
  isDaily: boolean;
  createdAt: string;
  updatedAt: string;
}

// Slash command menu items. Keep badges ASCII-only for the zero-emoji UI rule.
export interface SlashMenuItem {
  type: BlockType;
  label: string;
  description: string;
  icon: string;
}

export const SLASH_MENU_ITEMS: SlashMenuItem[] = [
  { type: 'text', label: 'Text', description: 'Plain paragraph', icon: 'TXT' },
  { type: 'heading1', label: 'Heading 1', description: 'Large section heading', icon: 'H1' },
  { type: 'heading2', label: 'Heading 2', description: 'Medium section heading', icon: 'H2' },
  { type: 'heading3', label: 'Heading 3', description: 'Small section heading', icon: 'H3' },
  { type: 'bullet', label: 'Bullet List', description: 'Unordered list item', icon: 'LIST' },
  { type: 'numbered', label: 'Numbered List', description: 'Ordered list item', icon: '1.' },
  { type: 'checkbox', label: 'Checkbox', description: 'Checkbox item', icon: 'TODO' },
  { type: 'code', label: 'Code Block', description: 'Monospace code', icon: '</>' },
  { type: 'quote', label: 'Quote', description: 'Block quote', icon: '"' },
  { type: 'divider', label: 'Divider', description: 'Horizontal rule', icon: 'LINE' },
  { type: 'image', label: 'Image', description: 'Embed an image by URL', icon: 'IMG' },
];

// Today's date for daily note
const TODAY = 'April 14, 2026';

// Generate unique IDs
let blockCounter = 0;
const genId = () => `b-${++blockCounter}-${Date.now()}`;
const genNoteId = () => `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// --- Mock Notes -------------------------------------------------------------

export const MOCK_NOTES: Note[] = [
  // 1. Daily note for today
  {
    id: 'note-daily',
    title: TODAY,
    blocks: [
      { id: genId(), type: 'heading1', content: TODAY },
      { id: genId(), type: 'text', content: 'Plan for today' },
      {
        id: genId(),
        type: 'bullet',
        content: 'Review sprint progress with team',
        indent: 0,
      },
      {
        id: genId(),
        type: 'bullet',
        content: 'Prepare investor update deck',
        indent: 0,
      },
      {
        id: genId(),
        type: 'bullet',
        content: 'Read [[Q2 Strategy]] before the meeting',
        indent: 0,
      },
      { id: genId(), type: 'divider', content: '' },
      { id: genId(), type: 'heading2', content: 'Notes' },
      { id: genId(), type: 'text', content: '' },
    ],
    pinned: false,
    isDaily: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },

  // 2. Q2 Strategy - has [[links]] to investor meeting prep
  {
    id: 'note-q2-strategy',
    title: 'Q2 Strategy',
    blocks: [
      { id: genId(), type: 'heading1', content: 'Q2 Strategy' },
      { id: genId(), type: 'text', content: 'Strategic priorities for Q2 2026.' },
      { id: genId(), type: 'heading2', content: 'Revenue Goals' },
      { id: genId(), type: 'bullet', content: 'MRR target: $75k (+56% from $48k)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'New logos: 12 enterprise accounts', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Expansion revenue: $15k', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Product Focus' },
      { id: genId(), type: 'numbered', content: 'API v2 launch (April 30)', indent: 0 },
      { id: genId(), type: 'numbered', content: 'Mobile app 2.0 (May 15)', indent: 0 },
      { id: genId(), type: 'numbered', content: 'AI summarization beta (June 1)', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Team' },
      { id: genId(), type: 'text', content: 'Hire 3 senior engineers and 1 designer. See [[Investor Meeting Prep]] for headcount plan.' },
      { id: genId(), type: 'quote', content: '"Q2 is about proving the model works at scale."' },
    ],
    pinned: true,
    isDaily: false,
    createdAt: '2026-03-15T10:00:00Z',
    updatedAt: '2026-04-10T14:30:00Z',
  },

  // 3. Meeting: Product Review Mar 12
  {
    id: 'note-meeting-mar12',
    title: 'Meeting: Product Review Mar 12',
    blocks: [
      { id: genId(), type: 'heading1', content: 'Meeting: Product Review' },
      { id: genId(), type: 'text', content: 'March 12, 2026 - Internal product review with engineering leads.' },
      { id: genId(), type: 'heading2', content: 'Attendees' },
      { id: genId(), type: 'bullet', content: 'Sarah (Engineering)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Marcus (Design)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Priya (Product)', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Agenda' },
      { id: genId(), type: 'numbered', content: 'Q1 metrics review', indent: 0 },
      { id: genId(), type: 'numbered', content: 'API v2 spec walkthrough', indent: 0 },
      { id: genId(), type: 'numbered', content: 'Q2 OKRs alignment', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Decisions' },
      { id: genId(), type: 'checkbox', content: 'Approve API v2 design - deferred to next week', checked: false },
      { id: genId(), type: 'checkbox', content: 'Mobile 2.0 scoping complete', checked: true },
      { id: genId(), type: 'checkbox', content: 'AI summarization go/no-go for June', checked: false },
      { id: genId(), type: 'heading2', content: 'Action Items' },
      { id: genId(), type: 'bullet', content: 'Priya: Finalize OKRs by Friday', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Marcus: Share mobile design tokens by Thursday', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Sarah: Post API v2 RFC in #engineering', indent: 0 },
      { id: genId(), type: 'divider', content: '' },
      { id: genId(), type: 'text', content: 'Next review: March 26.' },
    ],
    pinned: false,
    isDaily: false,
    createdAt: '2026-03-12T16:00:00Z',
    updatedAt: '2026-03-12T18:00:00Z',
  },

  // 4. Book notes: Zero to One
  {
    id: 'note-zero-to-one',
    title: 'Book notes: Zero to One',
    blocks: [
      { id: genId(), type: 'heading1', content: 'Book notes: Zero to One' },
      { id: genId(), type: 'text', content: 'Peter Thiel with Blake Masters. Notes taken over Q1 2026.' },
      { id: genId(), type: 'heading2', content: 'Core Thesis' },
      { id: genId(), type: 'quote', content: '"Every moment of failure is a chance to learn and find the one thing you did wrong."' },
      { id: genId(), type: 'heading2', content: 'Key Concepts' },
      { id: genId(), type: 'heading3', content: 'Zero to One' },
      { id: genId(), type: 'text', content: 'Going from 0 to 1 is creating something new. Going from 1 to n is copying what works.' },
      { id: genId(), type: 'heading3', content: 'Monopolies' },
      { id: genId(), type: 'text', content: 'Every successful business creates a new kind of wealth. Monopolists lie about their competition to avoid scrutiny.' },
      { id: genId(), type: 'heading3', content: 'The Seven Questions' },
      { id: genId(), type: 'numbered', content: 'The Engineering Question - can you create breakthrough technology vs. incremental?', indent: 0 },
      { id: genId(), type: 'numbered', content: 'The Timing Question - is now the right time to start?', indent: 0 },
      { id: genId(), type: 'numbered', content: 'The Monopoly Question - are you starting with a big share of a small market?', indent: 0 },
      { id: genId(), type: 'numbered', content: 'The People Question - can you find and retain the right team?', indent: 0 },
      { id: genId(), type: 'numbered', content: 'The Distribution Question - can you deliver your product?', indent: 0 },
      { id: genId(), type: 'numbered', content: 'The Durability Question - will your position be defensible in 10-20 years?', indent: 0 },
      { id: genId(), type: 'numbered', content: 'The Secret Question - have you identified an opportunity others haven\'t?', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Reflections' },
      { id: genId(), type: 'text', content: 'Relates strongly to what we discussed in [[Q1 Retrospective]] - we went from 1 to n too fast without owning a category.' },
      { id: genId(), type: 'code', content: '// Thiel\'s formula\nvalue = innovation - competition\n\n// For Signa:\n// - We ARE the innovation (AI PA)\n// - We need to own the category, not compete in email', language: 'js' },
    ],
    pinned: false,
    isDaily: false,
    createdAt: '2026-02-01T09:00:00Z',
    updatedAt: '2026-02-28T20:00:00Z',
  },

  // 5. API Design Patterns
  {
    id: 'note-api-patterns',
    title: 'API Design Patterns',
    blocks: [
      { id: genId(), type: 'heading1', content: 'API Design Patterns' },
      { id: genId(), type: 'text', content: 'Patterns I\'ve found useful across REST and GraphQL APIs.' },
      { id: genId(), type: 'heading2', content: 'Versioning' },
      { id: genId(), type: 'text', content: 'Use URL path for major versions. Never break existing endpoints.' },
      { id: genId(), type: 'code', content: 'GET /v1/users/:id\nGET /v2/users/:id  // new response shape\nPATCH /v2/users/:id  // additive only', language: 'js' },
      { id: genId(), type: 'heading2', content: 'Error Responses' },
      { id: genId(), type: 'text', content: 'Always return consistent error shape.' },
      { id: genId(), type: 'code', content: '{\n  "error": "NOT_FOUND",\n  "message": "User with id abc not found",\n  "status": 404,\n  "requestId": "req_12345"\n}', language: 'json' },
      { id: genId(), type: 'heading2', content: 'Pagination' },
      { id: genId(), type: 'bullet', content: 'Cursor-based for large datasets (emails, activity feeds)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Offset-based for small, bounded collections (users list)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Always return totalCount for offset pagination', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Rate Limiting' },
      { id: genId(), type: 'code', content: 'HTTP/1.1 429 Too Many Requests\nRetry-After: 3600\nX-RateLimit-Limit: 1000\nX-RateLimit-Remaining: 0', language: 'http' },
      { id: genId(), type: 'divider', content: '' },
      { id: genId(), type: 'quote', content: '"An API is a user interface for developers. Design it like you care."' },
    ],
    pinned: false,
    isDaily: false,
    createdAt: '2026-01-20T11:00:00Z',
    updatedAt: '2026-04-05T09:15:00Z',
  },

  // 6. Investor Meeting Prep - has [[links]]
  {
    id: 'note-investor-prep',
    title: 'Investor Meeting Prep',
    blocks: [
      { id: genId(), type: 'heading1', content: 'Investor Meeting Prep' },
      { id: genId(), type: 'text', content: 'Key metrics and talking points for the upcoming Series A conversation.' },
      { id: genId(), type: 'heading2', content: 'Financial Highlights' },
      { id: genId(), type: 'bullet', content: 'MRR: $48,000 (up 15% MoM)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Churn rate: 2.1% (target: <3%)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Net Revenue Retention: 118%', indent: 0 },
      { id: genId(), type: 'bullet', content: 'CAC payback: 8 months', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Use of Funds Ask' },
      { id: genId(), type: 'text', content: '$4M to expand engineering team (8 hires) and open London office.' },
      { id: genId(), type: 'heading2', content: 'Product Traction' },
      { id: genId(), type: 'bullet', content: '1,240 active users (up 22% MoM)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Daily active: 68% of MAU', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Mobile app: 4.7 star, 12k downloads', indent: 0 },
      { id: genId(), type: 'quote', content: '"Growth is slow because we are underfunded, not because the product is weak."' },
      { id: genId(), type: 'divider', content: '' },
      { id: genId(), type: 'text', content: 'See [[Q2 Strategy]] for how the investment deploys across product and hiring.' },
    ],
    pinned: true,
    isDaily: false,
    createdAt: '2026-04-08T08:00:00Z',
    updatedAt: '2026-04-12T16:45:00Z',
  },

  // 7. Q1 Retrospective
  {
    id: 'note-q1-retro',
    title: 'Q1 Retrospective',
    blocks: [
      { id: genId(), type: 'heading1', content: 'Q1 Retrospective' },
      { id: genId(), type: 'heading2', content: 'What went well' },
      { id: genId(), type: 'bullet', content: 'Onboarding flow redesign - 40% drop in day-1 churn', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Mobile app launch - 12k downloads, 4.7 star rating', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Integration with Gmail and M365 - shipped on time', indent: 0 },
      { id: genId(), type: 'bullet', content: 'AI summarization MVP - 3 beta customers using daily', indent: 0 },
      { id: genId(), type: 'heading2', content: 'What needs improvement' },
      { id: genId(), type: 'bullet', content: 'API latency - p95 at 800ms, should be <200ms', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Documentation gaps - 3 customer escalations from confusion', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Mobile notifications - missing in v1, expected in v2', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Key Lessons' },
      { id: genId(), type: 'numbered', content: 'Shipped too many features, not enough polish', indent: 0 },
      { id: genId(), type: 'numbered', content: 'Need dedicated design review before each release', indent: 0 },
      { id: genId(), type: 'numbered', content: 'Customer success feedback loop too slow', indent: 0 },
      { id: genId(), type: 'divider', content: '' },
      { id: genId(), type: 'text', content: 'Reading [[Book notes: Zero to One]] now - Thiel\'s framing of 0->1 vs 1->n is helping me think about what "proving the model" means for Signa.' },
    ],
    pinned: false,
    isDaily: false,
    createdAt: '2026-04-01T10:00:00Z',
    updatedAt: '2026-04-03T15:30:00Z',
  },

  // 8. Product Vision 2026
  {
    id: 'note-vision-2026',
    title: 'Product Vision 2026',
    blocks: [
      { id: genId(), type: 'heading1', content: 'Product Vision 2026' },
      { id: genId(), type: 'text', content: 'Where Signa goes from here - the 12-month product roadmap vision.' },
      { id: genId(), type: 'heading2', content: 'Mission' },
      { id: genId(), type: 'quote', content: '"The personal AI assistant that makes knowledge workers 10x more effective."' },
      { id: genId(), type: 'heading2', content: 'North Star Metrics' },
      { id: genId(), type: 'bullet', content: 'MAU: 10,000 by EOY', indent: 0 },
      { id: genId(), type: 'bullet', content: 'NRR: >120% (currently 118%)', indent: 0 },
      { id: genId(), type: 'bullet', content: 'Daily active users: 75% of MAU', indent: 0 },
      { id: genId(), type: 'heading2', content: 'Pillars' },
      { id: genId(), type: 'heading3', content: '1. Intelligence' },
      { id: genId(), type: 'text', content: 'AI that truly understands context - not just keyword matching. Summaries, action items, follow-up reminders that actually work.' },
      { id: genId(), type: 'heading3', content: '2. Speed' },
      { id: genId(), type: 'text', content: 'Every interaction under 200ms. Email sync under 5s. No waiting, no spinning.' },
      { id: genId(), type: 'heading3', content: '3. Privacy' },
      { id: genId(), type: 'text', content: 'Zero-knowledge architecture. User data never leaves their infrastructure unless they choose to sync.' },
      { id: genId(), type: 'image', content: '', imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80' },
      { id: genId(), type: 'divider', content: '' },
      { id: genId(), type: 'text', content: 'Revisit [[Q2 Strategy]] for the concrete H1 plan that maps to this vision.' },
    ],
    pinned: false,
    isDaily: false,
    createdAt: '2026-03-20T13:00:00Z',
    updatedAt: '2026-04-11T11:00:00Z',
  },
];

// Utility: get today's daily note title format
export function getDailyNoteTitle(): string {
  return TODAY;
}

// Utility: find backlinks for a note
export function findBacklinks(notes: Note[], targetNoteId: string): Note[] {
  return notes.filter((note) => {
    if (note.id === targetNoteId) return false;
    return note.blocks.some((block) => {
      if (!block.content) return false;
      const linkPattern = new RegExp(`\\[\\[([^\\]]+)\\]\\]`, 'g');
      let match;
      while ((match = linkPattern.exec(block.content)) !== null) {
        const linkedTitle = match[1]!;
        const target = notes.find((n) => n.id === targetNoteId);
        if (target && target.title === linkedTitle) return true;
      }
      return false;
    });
  });
}

// Utility: get preview text from first text block
export function getPreview(blocks: Block[]): string {
  const textBlock = blocks.find((b) => b.type === 'text' && b.content.trim());
  return textBlock?.content ?? '';
}

// Utility: format relative time
export function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
